import { OpenPassClient } from "@openpass/openpass-js-sdk";
import {
  buildOpenPassLoginPath,
  normalizeLanguagePrefix,
} from "./openpassPaths";

const CALLBACK_PATH = "/openpass-callback";

function getLoginUrl(callback, clientState) {
  return buildOpenPassLoginPath(
    clientState?.sudokuLanguagePrefix,
    callback.dataset.loginUrl,
  );
}

function safeRedirectUrl(candidate, fallback = "/") {
  try {
    const url = new URL(candidate || fallback, window.location.origin);
    return url.origin === window.location.origin ? url.href : fallback;
  } catch (error) {
    return fallback;
  }
}

function showInlineError(container) {
  const card = container.closest("[data-openpass-card]");
  const error = card?.querySelector("[data-openpass-error]");
  if (error) {
    error.hidden = false;
  }
}

async function renderInlineForm(container) {
  const clientId = container.dataset.clientId;
  if (!clientId || !container.id) {
    return;
  }

  const client = new OpenPassClient({ clientId });
  try {
    await client.renderInlineSignInForm({
      redirectUrl: `${window.location.origin}${CALLBACK_PATH}`,
      parentContainerElementId: container.id,
      authenticationMode: "redirect",
      signinButtonTextOption: container.dataset.buttonText || "signin",
      allowUnverifiedEmail: true,
      clientState: {
        sudokuLanguagePrefix: normalizeLanguagePrefix(
          container.dataset.languagePrefix,
        ),
      },
    });
  } catch (error) {
    console.error("Unable to render OpenPass", error);
    showInlineError(container);
  }
}

function showCallbackFailure(callback, redirectUrl) {
  const message = document.getElementById("openpass-callback-message");
  if (message) {
    message.textContent =
      callback.dataset.errorMessage || "Unable to complete sign in.";
  }

  window.setTimeout(() => {
    window.location.replace(
      safeRedirectUrl(redirectUrl, callback.dataset.homeUrl || "/"),
    );
  }, 3000);
}

async function retryWithVerifiedEmail(
  client,
  callback,
  signInResponse,
  redirectUrl,
) {
  const priorState = signInResponse.clientState || {};
  const options = {
    redirectUrl: `${window.location.origin}${CALLBACK_PATH}`,
    disableLoginHintEditing: true,
    allowUnverifiedEmail: false,
    clientState: {
      emailVerificationRetry: "true",
      sudokuLanguagePrefix: normalizeLanguagePrefix(
        priorState.sudokuLanguagePrefix,
      ),
      originalOriginatingUri:
        priorState.originalOriginatingUri || signInResponse.originatingUri,
    },
  };

  if (signInResponse.idToken?.email) {
    options.loginHint = signInResponse.idToken.email;
  }

  try {
    await client.signInWithRedirect(options);
  } catch (error) {
    console.error("Unable to restart OpenPass email verification", error);
    showCallbackFailure(callback, redirectUrl);
  }
}

async function completeCallback(callback) {
  const clientId = callback.dataset.clientId;
  const homeUrl = callback.dataset.homeUrl || "/";
  if (!clientId) {
    showCallbackFailure(callback, homeUrl);
    return;
  }

  const client = new OpenPassClient({ clientId });
  if (!client.isAuthenticationRedirect()) {
    showCallbackFailure(callback, homeUrl);
    return;
  }

  let signInResponse;
  try {
    signInResponse = await client.handleAuthenticationRedirect();
  } catch (error) {
    console.error("Unable to handle OpenPass authentication redirect", error);
    showCallbackFailure(callback, homeUrl);
    return;
  }

  const clientState = signInResponse.clientState || {};
  const redirectUrl = safeRedirectUrl(
    clientState.originalOriginatingUri || signInResponse.originatingUri,
    homeUrl,
  );

  try {
    const response = await fetch(getLoginUrl(callback, clientState), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": callback.dataset.csrfToken,
      },
      body: JSON.stringify({
        rawIdToken: signInResponse.rawIdToken,
        isQuickAuth: Boolean(clientState.isQuickAuth),
      }),
    });
    const body = await response.json().catch(() => ({}));

    if (
      response.status === 409 &&
      body.code === callback.dataset.verificationCode &&
      clientState.emailVerificationRetry !== "true"
    ) {
      await retryWithVerifiedEmail(
        client,
        callback,
        signInResponse,
        redirectUrl,
      );
      return;
    }

    if (!response.ok || !body.success) {
      showCallbackFailure(callback, redirectUrl);
      return;
    }

    window.location.replace(redirectUrl);
  } catch (error) {
    console.error("Unable to finish OpenPass authentication", error);
    showCallbackFailure(callback, redirectUrl);
  }
}

function initializeOpenPass() {
  const callback = document.getElementById("openpass-callback");
  if (callback) {
    completeCallback(callback);
    return;
  }

  document
    .querySelectorAll("[data-openpass-inline]")
    .forEach((container) => renderInlineForm(container));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeOpenPass, {
    once: true,
  });
} else {
  initializeOpenPass();
}
